import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import { Checkbox } from '../../../components/ui/Checkbox';

const MemberTable = ({ 
  members, 
  selectedMembers,
  onSelectMember,
  onSelectAll,
  onEdit,
  onViewHistory,
  onRenew,
  sortConfig,
  onSort,
  isLoading
}) => {
  const getStatusConfig = (status) => {
    switch (status) {
      case 'active':
        return { color: 'text-success', bg: 'bg-success/10', icon: 'CheckCircle', label: '활성' };
      case 'suspended':
        return { color: 'text-destructive', bg: 'bg-destructive/10', icon: 'Ban', label: '정지됨' };
      default:
        return { color: 'text-muted-foreground', bg: 'bg-muted', icon: 'Circle', label: '알 수 없음' };
    }
  };

  const getMembershipConfig = (type) => {
    switch (type) {
      case 'trial':
        return { label: '체험', color: 'text-warning' };
      case 'regular':
        return { label: '일반', color: 'text-primary' };
      default:
        return { label: '-', color: 'text-muted-foreground' };
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date?.toLocaleDateString('ko-KR', { 
      year: 'numeric', 
      month: 'long', 
      day: 'numeric'
    });
  };

  const handleSort = (key) => {
    onSort(key);
  };

  const isAllSelected = members?.length > 0 && selectedMembers?.length === members?.length;
  const isSomeSelected = selectedMembers?.length > 0 && selectedMembers?.length < members?.length;

  return (
    <>
      <div className="hidden lg:block overflow-x-auto scrollbar-custom">
        {isLoading && (
          <div className="text-center py-6">
            <p className="text-muted-foreground">불러오는 중...</p>
          </div>
        )}
        <table className="w-full border-collapse">
          <thead>
            <tr className="border-b border-border">
              <th className="text-left p-4 w-12">
                <Checkbox
                  checked={isAllSelected}
                  indeterminate={isSomeSelected}
                  onChange={(e) => onSelectAll(e?.target?.checked)}
                />
              </th>
              <th className="text-left p-4">
                <button
                  onClick={() => handleSort('name')}
                  className="flex items-center gap-2 font-heading font-medium text-foreground hover:text-primary transition-athletic"
                >
                  이름
                  <Icon 
                    name={sortConfig?.key === 'name' && sortConfig?.direction === 'asc' ? 'ChevronUp' : 'ChevronDown'} 
                    size={16} 
                  />
                </button>
              </th>
              <th className="text-left p-4">
                <span className="font-heading font-medium text-foreground">전화번호</span>
              </th>
              <th className="text-left p-4">
                <button
                  onClick={() => handleSort('skillLevel')}
                  className="flex items-center gap-2 font-heading font-medium text-foreground hover:text-primary transition-athletic"
                >
                  실력 레벨
                  <Icon 
                    name={sortConfig?.key === 'skillLevel' && sortConfig?.direction === 'asc' ? 'ChevronUp' : 'ChevronDown'} 
                    size={16} 
                  />
                </button>
              </th>
              <th className="text-left p-4">
                <span className="font-heading font-medium text-foreground">회원권</span>
              </th>
              <th className="text-left p-4">
                <button
                  onClick={() => handleSort('status')}
                  className="flex items-center gap-2 font-heading font-medium text-foreground hover:text-primary transition-athletic"
                >
                  상태
                  <Icon 
                    name={sortConfig?.key === 'status' && sortConfig?.direction === 'asc' ? 'ChevronUp' : 'ChevronDown'} 
                    size={16} 
                  />
                </button>
              </th>
              <th className="text-left p-4">
                <button
                  onClick={() => handleSort('registeredAt')}
                  className="flex items-center gap-2 font-heading font-medium text-foreground hover:text-primary transition-athletic"
                >
                  등록일
                  <Icon 
                    name={sortConfig?.key === 'registeredAt' && sortConfig?.direction === 'asc' ? 'ChevronUp' : 'ChevronDown'} 
                    size={16} 
                  />
                </button>
              </th>
              <th className="text-right p-4">
                <span className="font-heading font-medium text-foreground">작업</span>
              </th>
            </tr>
          </thead>
          <tbody>
            {members?.map((member) => {
              const statusConfig = getStatusConfig(member?.status);
              const membershipConfig = getMembershipConfig(member?.membershipType);
              const isSelected = selectedMembers?.includes(member?.id);

              return (
                <tr 
                  key={member?.id}
                  className="border-b border-border hover:bg-muted/50 transition-athletic"
                >
                  <td className="p-4">
                    <Checkbox
                      checked={isSelected}
                      onChange={(e) => onSelectMember(member?.id, e?.target?.checked)}
                    />
                  </td>
                  <td className="p-4">
                    <div className="font-medium text-foreground">{member?.name}</div>
                  </td>
                  <td className="p-4">
                    <span className="data-text text-foreground">{member?.phone}</span>
                  </td>
                  <td className="p-4">
                    <div className="flex items-center gap-2">
                      <div className="w-12 h-12 rounded-md bg-primary/10 flex items-center justify-center">
                        <span className="text-lg font-heading font-bold text-primary data-text">
                          {member?.skillLevel}
                        </span>
                      </div>
                      <span className="text-sm caption text-muted-foreground">{member?.skillDescription}</span>
                    </div>
                  </td>
                  <td className="p-4">
                    <div>
                      <span className={`font-medium ${membershipConfig?.color}`}>
                        {membershipConfig?.label}
                      </span>
                      {member?.membershipType === 'regular' && member?.membershipExpiry && (
                        <div className="text-sm caption text-muted-foreground">
                          만료: {formatDate(member?.membershipExpiry)}
                        </div>
                      )}
                    </div>
                  </td>
                  <td className="p-4">
                    <div className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-md ${statusConfig?.bg}`}>
                      <Icon name={statusConfig?.icon} size={14} />
                      <span className={`text-sm font-medium ${statusConfig?.color}`}>
                        {statusConfig?.label}
                      </span>
                    </div>
                  </td>
                  <td className="p-4">
                    <span className="text-sm caption text-muted-foreground">
                      {formatDate(member?.registeredAt)}
                    </span>
                  </td>
                  <td className="p-4">
                    <div className="flex items-center justify-end gap-2">
                      <Button
                        variant="ghost"
                        size="sm"
                        iconName="Edit"
                        onClick={() => onEdit(member)}
                      />
                      <Button
                        variant="ghost"
                        size="sm"
                        iconName="History"
                        onClick={() => onViewHistory(member)}
                      />
                      {member?.membershipType !== 'none' && (
                        <Button
                          variant="ghost"
                          size="sm"
                          iconName="RefreshCw"
                          onClick={() => onRenew(member)}
                        />
                      )}
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>

        {!isLoading && members?.length === 0 && (
          <div className="text-center py-12">
            <Icon name="Users" size={48} className="mx-auto mb-4 text-muted-foreground" />
            <p className="text-muted-foreground">회원이 없습니다</p>
          </div>
        )}
      </div>
      <div className="lg:hidden space-y-4">
        {isLoading && (
          <div className="text-center py-6">
            <p className="text-muted-foreground">불러오는 중...</p>
          </div>
        )}
        {members?.map((member) => {
          const statusConfig = getStatusConfig(member?.status);
          const membershipConfig = getMembershipConfig(member?.membershipType);
          const isSelected = selectedMembers?.includes(member?.id);

          return (
            <div 
              key={member?.id}
              className="bg-card border border-border rounded-xl p-4 space-y-3"
            >
              <div className="flex items-start gap-3">
                <Checkbox
                  checked={isSelected}
                  onChange={(e) => onSelectMember(member?.id, e?.target?.checked)}
                  className="mt-1"
                />
                <div className="flex-1">
                  <h3 className="font-heading font-medium text-foreground mb-1">
                    {member?.name}
                  </h3>
                  <p className="text-sm data-text text-muted-foreground">{member?.phone}</p>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 rounded-md bg-primary/10 flex items-center justify-center">
                  <span className="text-lg font-heading font-bold text-primary data-text">
                    {member?.skillLevel}
                  </span>
                </div>
                <div className="flex-1">
                  <p className="text-sm caption text-muted-foreground">{member?.skillDescription}</p>
                  <p className={`text-sm font-medium ${membershipConfig?.color} mt-1`}>
                    {membershipConfig?.label} 회원권
                  </p>
                </div>
              </div>
              <div className="flex flex-wrap gap-2">
                <div className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-md ${statusConfig?.bg}`}>
                  <Icon name={statusConfig?.icon} size={14} />
                  <span className={`text-sm font-medium ${statusConfig?.color}`}>
                    {statusConfig?.label}
                  </span>
                </div>
              </div>
              <p className="text-sm caption text-muted-foreground">
                등록일: {formatDate(member?.registeredAt)}
              </p>
              {member?.membershipType === 'regular' && member?.membershipExpiry && (
                <p className="text-sm caption text-muted-foreground">
                  회원권 만료: {formatDate(member?.membershipExpiry)}
                </p>
              )}
              <div className="flex gap-2 pt-2 border-t border-border">
                <Button
                  variant="outline"
                  size="sm"
                  iconName="Edit"
                  iconPosition="left"
                  onClick={() => onEdit(member)}
                  fullWidth
                >
                  수정
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  iconName="History"
                  iconPosition="left"
                  onClick={() => onViewHistory(member)}
                  fullWidth
                >
                  기록
                </Button>
                {member?.membershipType === 'regular' && (
                  <Button
                    variant="outline"
                    size="sm"
                    iconName="RefreshCw"
                    onClick={() => onRenew(member)}
                  />
                )}
              </div>
            </div>
          );
        })}

        {!isLoading && members?.length === 0 && (
          <div className="text-center py-12">
            <Icon name="Users" size={48} className="mx-auto mb-4 text-muted-foreground" />
            <p className="text-muted-foreground">회원이 없습니다</p>
          </div>
        )}
      </div>
    </>
  );
};

export default MemberTable;